import { css, keyframes } from "@emotion/css";
import { THEME_COLOR } from "../styles/colors";

// Shared animations for the app.

// ─── Pulse ───────────────────────────────────────────────────────────────────

// 1.3s ease-in-out is the intended loop cadence.
const pulse = keyframes({
  "0%, 100%": { opacity: 1 },
  "50%": { opacity: 0.35 },
});

/**
 * Pulsing opacity animation. Stagger elements by passing a different `delayMs`.
 */
export const pulseClass = (delayMs) =>
  css({ animation: `${pulse} 1.3s ease-in-out ${delayMs}ms infinite` });

// ─── Shared timing ──────────────────────────────────────────────────────────

// How long a newly added row/card stays highlighted. Consumed by BOTH highlight treatments below
// (flashClass and addedRowHighlightClass). Shared by both session tabs: it drives the
// CSS animations below AND the JS timeouts that clear highlight state (agenda.hooks.js,
// ActionItems.jsx), so the paint and the state always end together.
export const ADDED_ROW_HIGHLIGHT_DURATION_MS = 2500;

// ─── Flash ───────────────────────────────────────────────────────────────────

// One-shot highlight: start tinted, fade to transparent. Used to briefly flag a newly added row.
const flash = keyframes({
  "0%": { opacity: 0.45 },
  "100%": { opacity: 0 },
});

/**
 * One-shot fade-out for a highlight overlay: runs once and holds the final (transparent) state.
 * The overlay's colour/position are set by the caller; this only animates the opacity.
 */
export const flashClass = css({
  animation: `${flash} ${ADDED_ROW_HIGHLIGHT_DURATION_MS}ms ease forwards`,
});

// ─── Added-item highlight: pill + accent bar ─────────────────────────────────

// Mint, matching the Action Items overlay. The `flash` keyframes start it at 45% and ease to
// transparent, so this is the fill colour rather than the rendered strength.
const HIGHLIGHT_TINT = THEME_COLOR.MINT_GREEN;
// How far the pill extends above/below the row's text box.
const HIGHLIGHT_INSET = 6;
const HIGHLIGHT_ACCENT_WIDTH = 3;
const HIGHLIGHT_RADIUS = 8;   // matches the Action Items overlay

// The accent bar is agenda's own addition (Action Items has no bar); it uses the tile accent
// colour Action Items applies to its cards, and fades on the same curve as the pill.
const ACCENT_BAR_COLOR = THEME_COLOR.TEAL_GREEN;

/**
 * One-shot highlight for a newly added row: mint pill + leading accent bar, then fade.
 * Colour, opacity curve and duration match the Action Items overlay; the pill paints BEHIND the row
 * (zIndex -1) rather than over it, so the title stays readable while it fades.
 */
export const addedRowHighlightClass = css({
  position: "relative",
  isolation: "isolate",
  "&::before": {
    content: "''",
    position: "absolute",
    top: -HIGHLIGHT_INSET,
    bottom: -HIGHLIGHT_INSET,
    left: 0,
    right: 0,
    borderRadius: HIGHLIGHT_RADIUS,
    zIndex: -1,
    backgroundColor: HIGHLIGHT_TINT,
    // Same `flash` keyframes and easing the Action Items overlay uses, so both tabs fade
    // identically (starts at 45% and eases to transparent) from one definition.
    animation: `${flash} ${ADDED_ROW_HIGHLIGHT_DURATION_MS}ms ease forwards`,
  },
  // Accent bar — only outer (left) corners rounded so inner edge stays a clean vertical.
  "&::after": {
    content: "''",
    position: "absolute",
    top: -HIGHLIGHT_INSET,
    bottom: -HIGHLIGHT_INSET,
    left: 0,
    width: HIGHLIGHT_ACCENT_WIDTH,
    borderTopLeftRadius: HIGHLIGHT_RADIUS,
    borderBottomLeftRadius: HIGHLIGHT_RADIUS,
    zIndex: -1,
    backgroundColor: ACCENT_BAR_COLOR,
    animation: `${flash} ${ADDED_ROW_HIGHLIGHT_DURATION_MS}ms ease forwards`,
  },
  // Reduced motion: skip the fade but still end where the animation would — the fill colour is on
  // the base rule now, so "animation: none" alone would leave the highlight painted permanently.
  "@media (prefers-reduced-motion: reduce)": {
    "&::before": { animation: "none", opacity: 0 },
    "&::after": { animation: "none", opacity: 0 },
  },
});